import { RequestedFeature } from "@vixi/types";
import { ComponentProps, FC, forwardRef, ReactNode } from "react";
import { useTranslation } from "@vixi/localization";
import { NavigateOptions } from "@vixi/router";
import { useUpvoteFeature, useDownvoteFeature } from "@vixi/api";
import { cn } from "@/lib";
import { ButtonProps, Button } from "../base/button";
import { TextLink } from "../links";
import { AttachedFilesIndicator } from "../loading/AttachedFilesIndicator";

// Icons
import UpvoteStrokeIcon from "@assets/icons/upvote-stroke.svg?react";

// Constants
const featureListItemClasses = {
  root: "FeatureListItem-root",
  viewDetailsBtn: "FeatureListItem-viewDetailsButton",
  upvoteButton: "FeatureListItem-upvoteButton",
  upvoteIcon: "FeatureListItem-upvoteIcon",
} as const;

// Types
interface FeatureListItemProps {
  feature: RequestedFeature;
}

interface BaseFeatureListItemProps extends FeatureListItemProps {
  from: Extract<"/feedback" | "/admin/feature-requests", NavigateOptions["from"]>;
  actions: ReactNode;
}

type UpvoteButtonProps = ButtonProps & {
  isVoted?: boolean;
};

const FeatureListItemRoot = forwardRef<HTMLDivElement, ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      className={cn(
        "border-divider flex justify-between border-t-2 bg-transparent px-6.75 py-5",
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
);

const UpvoteButton = forwardRef<HTMLButtonElement, UpvoteButtonProps>(
  ({ className, isVoted, ...props }, ref) => (
    <Button
      ref={ref}
      className={cn(
        "fs-16 text-foreground hover:bg-paper hover:text-foreground min-w-22.5 justify-end px-3.5 py-2.75 font-medium",
        className,
      )}
      endIcon={<UpvoteStrokeIcon className={cn(isVoted && "fill-primary")} />}
      variant="outlined"
      {...props}
    />
  ),
);

const BaseFeatureListItem: FC<BaseFeatureListItemProps> = ({ from, feature, actions }) => {
  const { t } = useTranslation();

  return (
    <FeatureListItemRoot className={featureListItemClasses.root}>
      <div>
        <p className="fs-18">{feature.title}</p>
        <p className="text-foreground-secondary">{feature.details}</p>
        <p>
          <TextLink
            replace
            from={from}
            search={{
              featureId: feature.id,
            }}
            className={cn(featureListItemClasses.viewDetailsBtn)}
          >
            {t("Action.ReadMore")}
          </TextLink>
          {!!feature.attachments.length && (
            <AttachedFilesIndicator count={feature.attachments.length} />
          )}
        </p>
      </div>

      {actions}
    </FeatureListItemRoot>
  );
};

const FeatureListItem: FC<FeatureListItemProps> = ({ feature }) => {
  const { tNum } = useTranslation();

  const { mutateAsync: upvoteFeature } = useUpvoteFeature();
  const { mutateAsync: downvoteFeature } = useDownvoteFeature();

  const onClickUpvote = async (feature: RequestedFeature) => {
    if (feature.isVoted) {
      await downvoteFeature(feature.id);
    } else {
      await upvoteFeature(feature.id);
    }
  };

  return (
    <BaseFeatureListItem
      from="/feedback"
      feature={feature}
      actions={
        <div className="flex items-center gap-2.75">
          <UpvoteButton isVoted={feature.isVoted} onClick={() => onClickUpvote(feature)}>
            {tNum(feature.votes)}
          </UpvoteButton>
        </div>
      }
    />
  );
};

export { BaseFeatureListItem, FeatureListItem, UpvoteButton };
export type { BaseFeatureListItemProps, FeatureListItemProps };
